import { Component, DestroyRef, OnInit, WritableSignal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { ToastService } from '@storybook/services/toast/toast.service';
import { finalize, startWith } from 'rxjs';
import { MaxxBrandKit } from '../maxx-settings.model';
import { MaxxSettingsService, maxxSettingsErrorMessage } from '../maxx-settings.service';

const PDQ_DESIGN = 'pdq';
const BRAND_DESIGN = 'brand';
const PDQ_PRIMARY_COLOR = '#884DFF';
const PDQ_INK_COLOR = '#241434';
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const MAX_LOGO_BYTES = 512 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const LIGHT_BAR_MIX_PERCENT = 14;

const PREVIEW_CARDS = [
  {
    caption: 'PDF report',
    title: 'Checkout performance, September',
    slide: false,
    bars: [24, 34, 28, 46, 56].map((height, index) => ({ height, strong: index >= 3 })),
  },
  {
    caption: 'Slide',
    title: 'What to test next',
    slide: true,
    bars: [58, 40, 26].map((height, index) => ({ height, strong: index === 0 })),
  },
];

interface LogoDraft {
  preview: string | null;
  file?: File;
}

@Component({
  selector: 'pdq-maxx-brand-kit-settings',
  templateUrl: './maxx-brand-kit-settings.component.html',
  styleUrls: ['./maxx-brand-kit-settings.component.scss', '../../settings.scss'],
  standalone: false,
})
export class MaxxBrandKitSettingsComponent implements OnInit {
  private readonly maxxSettingsService = inject(MaxxSettingsService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly logoAccept = LOGO_TYPES.join(',');
  readonly previewCards = PREVIEW_CARDS;
  readonly brandNameControl = new UntypedFormControl('');
  readonly primaryColorControl = new UntypedFormControl(PDQ_PRIMARY_COLOR);
  readonly inkColorControl = new UntypedFormControl(PDQ_INK_COLOR);

  readonly saved = signal<MaxxBrandKit | null>(null);
  readonly enabled = signal(false);
  readonly logo = signal<LogoDraft>({ preview: null });
  readonly logoOnDark = signal<LogoDraft>({ preview: null });
  readonly brandName = signal('');
  readonly primaryColor = signal(PDQ_PRIMARY_COLOR);
  readonly inkColor = signal(PDQ_INK_COLOR);
  readonly isLoading = signal(true);
  readonly loadFailed = signal(false);
  readonly isSaving = signal(false);
  readonly logoError = signal<string | null>(null);
  private readonly logoReads = { logo: 0, logoOnDark: 0 };

  readonly designOptions = computed<DropdownOption[]>(() => [
    { label: 'PDQ design', value: PDQ_DESIGN },
    { label: `${this.saved()?.storeName || 'Your'} design`, value: BRAND_DESIGN },
  ]);
  readonly selectedDesign = computed(() => this.designOptions()[this.enabled() ? 1 : 0]);

  readonly previewPrimary = computed(() =>
    this.enabled() && HEX_COLOR.test(this.primaryColor()) ? this.primaryColor() : PDQ_PRIMARY_COLOR,
  );
  readonly previewInk = computed(() =>
    this.enabled() && HEX_COLOR.test(this.inkColor()) ? this.inkColor() : PDQ_INK_COLOR,
  );
  readonly previewLight = computed(
    () => `color-mix(in srgb, ${this.previewPrimary()} ${LIGHT_BAR_MIX_PERCENT}%, #fff)`,
  );
  readonly previewText = computed(() => (this.enabled() ? this.previewInk() : this.previewPrimary()));
  readonly previewLabel = computed(() => (this.enabled() ? this.brandName() || 'Brand' : 'PDQ'));
  readonly previewLogo = computed(() => (this.enabled() ? this.logo().preview : null));

  readonly colorsValid = computed(() => HEX_COLOR.test(this.primaryColor()) && HEX_COLOR.test(this.inkColor()));
  readonly needsLogo = computed(() => this.enabled() && !this.logo().preview);
  readonly needsBrandName = computed(() => this.enabled() && !this.brandName().trim());

  readonly hasChanges = computed(() => {
    const saved = this.saved();
    if (!saved) return false;
    if (this.enabled() !== saved.enabled) return true;
    if (!this.enabled()) return false;
    return (
      this.brandName() !== saved.brandName ||
      this.primaryColor().toUpperCase() !== saved.primaryColor.toUpperCase() ||
      this.inkColor().toUpperCase() !== saved.inkColor.toUpperCase() ||
      Boolean(this.logo().file) ||
      Boolean(this.logoOnDark().file) ||
      this.logoOnDark().preview !== saved.logoOnDarkUrl
    );
  });
  readonly canSave = computed(
    () => !this.enabled() || (this.colorsValid() && !this.needsLogo() && !this.needsBrandName()),
  );

  ngOnInit(): void {
    this.bindControl(this.brandNameControl, this.brandName);
    this.bindControl(this.primaryColorControl, this.primaryColor);
    this.bindControl(this.inkColorControl, this.inkColor);

    this.maxxSettingsService
      .getBrandKit()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (kit) => this.applySaved(kit),
        error: () => this.loadFailed.set(true),
      });
  }

  selectDesign(option: DropdownOption | null): void {
    if (option && !this.isSaving()) this.enabled.set(option.value === BRAND_DESIGN);
  }

  pickLogo(event: Event, target: 'logo' | 'logoOnDark'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.isSaving()) return;
    if (!LOGO_TYPES.includes(file.type) || file.size > MAX_LOGO_BYTES) {
      this.logoError.set('The logo must be a PNG, JPEG or WebP image up to 512 KB.');
      return;
    }
    this.logoError.set(null);
    const read = ++this.logoReads[target];
    const reader = new FileReader();
    reader.onload = () => {
      if (read !== this.logoReads[target]) return;
      const draft = { preview: String(reader.result), file };
      if (target === 'logo') this.logo.set(draft);
      else this.logoOnDark.set(draft);
    };
    reader.readAsDataURL(file);
  }

  removeLogoOnDark(): void {
    if (this.isSaving()) return;
    this.logoReads.logoOnDark++;
    this.logoOnDark.set({ preview: null });
  }

  cancel(): void {
    this.logoReads.logo++;
    this.logoReads.logoOnDark++;
    const saved = this.saved();
    if (saved) this.applySaved(saved);
    this.logoError.set(null);
  }

  save(): void {
    if (!this.canSave() || this.isSaving()) return;
    const saved = this.saved();
    this.isSaving.set(true);
    this.maxxSettingsService
      .saveBrandKit({
        enabled: this.enabled(),
        brandName: this.brandName().trim(),
        primaryColor: this.primaryColor(),
        inkColor: this.inkColor(),
        logo: this.logo().file,
        logoOnDark: this.logoOnDark().file,
        removeLogoOnDark: Boolean(saved?.logoOnDarkUrl) && !this.logoOnDark().preview,
      })
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (kit) => {
          this.applySaved(kit);
          this.toastService.showToast({ mode: 'info', message: 'Settings saved successfully' });
        },
        error: (error) => {
          const message = maxxSettingsErrorMessage(error);
          this.toastService.showToast({ mode: 'danger', message: `Failed to save settings. ${message}` });
        },
      });
  }

  private applySaved(kit: MaxxBrandKit): void {
    this.saved.set(kit);
    this.enabled.set(kit.enabled);
    this.logo.set({ preview: kit.logoUrl });
    this.logoOnDark.set({ preview: kit.logoOnDarkUrl });
    this.brandNameControl.setValue(kit.brandName);
    this.primaryColorControl.setValue(kit.primaryColor);
    this.inkColorControl.setValue(kit.inkColor);
  }

  private bindControl(control: UntypedFormControl, target: WritableSignal<string>): void {
    control.valueChanges
      .pipe(startWith(control.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => target.set(String(value ?? '')));
  }
}
